import {
  Alerts,
  AlertsContext,
  Box,
  Button,
  Flex,
  Form,
  Text,
  useFormState,
  useFormValidation,
  useBackendErrorMessage,
} from 'dash-ui';
import { WeekDays } from 'dash-common';
import { FormEvent, useContext, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import {
  GetPropertyByIdQuery,
  useSetAdvancedPropertyDetailsMutation,
} from 'gql/generated.graphql';
import { ChangesConfirmationModal } from 'containers/properties/components/ChangesConfirmationModal';

import {
  FormFields,
  FormFieldsList,
} from './AdvancedPropertyDetails.constants';
import { compareAdvancedPropertyData } from './AdvancedPropertyDetails.helpers';
import { TFormHandlers } from './AdvancedPropertyDetails.interfaces';
import BuildingDetailsFormElements from './components/BuildingDetailsFormElements/BuildingDetailsFormElements';
import DescriptionFormElements from './components/DescriptionFormElements/DescriptionFormElements';
import SalesWorkingHours from './components/SalesWorkingHours/SalesWorkingHours';
import { TopBarWrapper } from './AdvancedPropertyDetails.styles';

const AdvancedPropertyDetails = ({
  property,
}: {
  property: GetPropertyByIdQuery['getPropertyById'];
}) => {
  const { t } = useTranslation(['common', 'pages']);
  const translate = useBackendErrorMessage();
  const { toggleAlert } = useContext(AlertsContext);
  const [
    formState,
    [setWorkingHours, ...stateHandlers],
    [formOnSubmit, formOnError, formOnSuccess],
  ] = useFormState([FormFields.Working_Hours, ...FormFieldsList], property);
  const [formErrors, , [setWorkingHoursError, ...errorHandlers]] =
    useFormValidation([FormFields.Working_Hours, ...FormFieldsList]);
  const formRef = useRef<HTMLFormElement>(null);
  const formHandlers: TFormHandlers = FormFieldsList.reduce(
    (memo, field, index) => ({
      ...memo,
      [field]: {
        setState: stateHandlers[index],
        setError: errorHandlers[index],
      },
    }),
    {}
  );
  const [setAdvancedPropertyMutation, { loading }] =
    useSetAdvancedPropertyDetailsMutation();

  useEffect(() => {
    // FIXME: re-creating context functions everytime state change. Use useCallback
    setWorkingHours(property.workingHours);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [property]);

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    e.stopPropagation();
    formOnSubmit();

    const input: any = Object.values(FormFields).reduce(
      (memo, key) => ({ ...memo, [key]: formState[key] }),
      { propertyId: property._id }
    );

    if (input.workingHours) {
      input.workingHours = Object.values(WeekDays).reduce(
        (memo: any, key: string) => {
          const { to, from } = input.workingHours[key] || {};

          return { ...memo, ...(to && from && { [key]: { to, from } }) };
        },
        {}
      );
    }

    setAdvancedPropertyMutation({
      variables: { input },
      onCompleted: () => {
        formOnSuccess();
        toggleAlert(Alerts.ChangesSaved, true);
      },
      onError: (error) => {
        formOnError(translate(error));
      },
    });
  };

  const isEqual = compareAdvancedPropertyData(property, formState);

  return (
    <Flex pb={6} flexDirection="column" w="100%">
      <ChangesConfirmationModal condition={isEqual} />
      <Form w="100%" ref={formRef} onSubmit={onSubmit}>
        <TopBarWrapper
          mx={6}
          justifyContent="space-between"
          alignItems="center"
          pos="sticky"
          h={48}
          top={245}
        >
          <Text semibold lg>{t('common:labels.description')}</Text>
          <Button
            px={5.5}
            medium
            type="submit"
            disabled={loading || isEqual}
            isLoading={loading}
            onClick={() => {
              if (formRef.current) {
                formRef.current.requestSubmit();
              }
            }}
          >
            {t('common:labels.saveChanges')}
          </Button>
        </TopBarWrapper>
        <DescriptionFormElements
          formErrors={formErrors}
          formHandlers={formHandlers}
          formState={formState}
        />
        <Box mx={6} mt={4}>
          <Text semibold lg>{t('pages:propertyPage.buildingDetails')}</Text>
        </Box>
        <BuildingDetailsFormElements
          formErrors={formErrors}
          formHandlers={formHandlers}
          formState={formState}
          disabled
        />
        <Box mx={6} mt={4}>
          <Text semibold lg>{t('pages:propertyPage.salesGalleryHours')}</Text>
        </Box>
        {/* TODO: Replace SalesWorkingHours by WorkingWeekHoursInput from dash-ui */}
        <SalesWorkingHours
          disabled
          setWorkingHours={setWorkingHours}
          setWorkingHoursError={setWorkingHoursError}
          formErrors={formErrors}
          formState={formState}
        />
      </Form>
    </Flex>
  );
};

export default AdvancedPropertyDetails;
